import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleUserRound } from 'lucide-react'
import { useAssigneeLoad } from '../hooks/useAssigneeLoad'
import type { AssigneeComboBoxProps } from '../type'
import { ComboBox } from '@/components/app/combo-box/ComboBox'
import { useComboBox } from '@/hooks/useComboBox'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import { Button } from '@/components/ui/button'
import useLoginStore from '@/store/LoginStore'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

// Helper function to get user initials
function getInitials(user: {
  name?: string | null
  email?: string | null
}): string {
  if (user.name) {
    return user.name.slice(0, 2).toUpperCase()
  }
  if (user.email) {
    return user.email.slice(0, 2).toUpperCase()
  }
  return 'U'
}

// Helper function to get user display name
function getDisplayName(user: {
  name?: string | null
  email?: string | null
}): string {
  if (user.name) return user.name
  if (user.email) return user.email
  return 'Unknown User'
}

export function AssigneeComboBox({
  selectedUserId,
  onUserChange,
  placeholderText,
  noneLabel,
  Icon = CircleUserRound,
  searchText,
  buttonClassName,
  isDisabled = false,
  allowSuperAgent = false,
  fontSize = 'xs',
  popoverOpen,
  onPopoverOpenChange,
  triggerProps,
  projectId,
  triggerContent,
}: AssigneeComboBoxProps) {
  const { t } = useTranslation('common')
  const noneText = noneLabel ?? t('picker.assignee.none')
  const { users, isLoading } = useMembers()
  const currentUserId = useLoginStore((state) => state.userId)

  const {
    isOpen,
    setIsOpen,
    handleValueChange,
    currentValue,
  } = useComboBox({
    selectedValue: selectedUserId,
    onValueChange: onUserChange,
    popoverOpen,
    onPopoverOpenChange,
  })
  const { openCountByUser, activeInProject } = useAssigneeLoad(projectId, isOpen)

  // The Super Agent is a workspace-level member, so it reaches every project's picker —
  // but it only does work on projects that enabled it (elsewhere the assignment is a dead
  // end the backend now rejects). Hide it unless this project allows it.
  //
  // One exception: keep it when it IS the current assignee. A project can be offboarded
  // while tasks are still assigned to the agent, and dropping the option there would
  // render the trigger as "No assignee" and hide the very value the user needs to change.
  //
  // FEATURE_FLAGS.ENABLE_SUPER_AGENT overrides every caller: while Super Agent is hidden
  // the agent is never offered as a new choice (the current-assignee exception still holds).
  const superAgentSelectable = FEATURE_FLAGS.ENABLE_SUPER_AGENT && allowSuperAgent
  const selectableUsers = useMemo(
    () =>
      superAgentSelectable
        ? users
        : users.filter((user) => !user.isSuperAgent || user.userId === selectedUserId),
    [users, superAgentSelectable, selectedUserId],
  )

  const userOptions = useMemo(() => {
    const textSizeClass =
      fontSize === 'xs'
        ? 'text-xs'
        : fontSize === 'sm'
          ? 'text-sm'
          : 'text-base'

    // "No assignee" is the first row, outside the groups.
    const noAssigneeOption = {
      value: '',
      labelText: noneText,
      icon: <Icon className="size-4 text-muted-foreground" />,
      label: (
        <span className="flex items-center gap-2">
          <Icon className="size-4" />
          <span className={textSizeClass}>{noneText}</span>
        </span>
      ),
    }

    // Suggested (design 31b): you, plus whoever has open work in this project.
    // Everyone else follows alphabetically.
    const isSuggested = (userId: string) =>
      userId === currentUserId || activeInProject.has(userId)
    const sortedUsers = [...selectableUsers].sort((a, b) => {
      if (a.userId === currentUserId) return -1
      if (b.userId === currentUserId) return 1
      const suggested = Number(isSuggested(b.userId)) - Number(isSuggested(a.userId))
      if (suggested !== 0) return suggested
      return getDisplayName(a).localeCompare(getDisplayName(b))
    })

    const userOptionsList = sortedUsers.map((user) => {
      const initials = getInitials(user)
      const displayName = getDisplayName(user)
      const isMe = user.userId === currentUserId
      const openCount = openCountByUser.get(user.userId) ?? 0

      const avatarEl = (
        <Avatar className="size-[18px]">
          <AvatarImage
            src={memberAvatarSrc(user)}
            alt={displayName}
          />
          <AvatarFallback className="text-[9px]">{initials}</AvatarFallback>
        </Avatar>
      )

      return {
        value: user.userId,
        labelText: displayName,
        icon: avatarEl,
        group: isSuggested(user.userId)
          ? t('picker.assignee.suggested')
          : t('picker.assignee.everyone'),
        meta: isMe
          ? t('picker.assignee.you')
          : openCount > 0
            ? t('picker.assignee.open', { count: openCount })
            : undefined,
        label: (
          <span className="flex items-center gap-2">
            {avatarEl}
            <span className={textSizeClass}>{displayName}</span>
          </span>
        ),
        triggerLabel: isMe ? (
          <span className="flex items-center gap-2">
            {avatarEl}
            <span className={textSizeClass}>{t('picker.assignee.me')}</span>
          </span>
        ) : undefined,
      }
    })

    return [noAssigneeOption, ...userOptionsList]
  }, [selectableUsers, fontSize, noneText, Icon, currentUserId, openCountByUser, activeInProject, t])

  if (isLoading) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className={cn(
          'h-6 w-auto min-w-0 flex-none rounded-sm border border-border bg-background px-2 text-xs',
          buttonClassName,
        )}
      >
        <CircleUserRound className="size-3.5 mr-1" />
        {t('states.loading')}
      </Button>
    )
  }

  // Always show a single ComboBox - like Linear
  // If user is not found (invalid userId), treat as "No assignee"

  return (
    <ComboBox
      placeholderText={
        <span className="flex items-center gap-1.5">
          <Icon className="size-3.5" />
          {placeholderText ?? t('picker.assignee.placeholder')}
        </span>
      }
      searchText={searchText ?? t('picker.assignee.search')}
      options={userOptions}
      defaultSelectedValue={currentValue}
      isDisabled={isDisabled}
      onChange={handleValueChange}
      buttonClassName={cn(
        'h-6 w-auto min-w-0 flex-none rounded-sm border border-border bg-background px-2 text-xs font-medium text-foreground hover:border-muted-foreground/40 hover:bg-accent',
        buttonClassName,
      )}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      triggerProps={{ 'data-testid': 'assignee-combobox', ...triggerProps }}
      triggerContent={triggerContent}
    />
  )
}
